import { useFormikContext } from 'formik'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'
import * as mui from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import React from 'react'

import { translationsContext } from 'context/translations'
import * as schema from 'types/graphql/schema'
import { volumeSx } from 'utils/style'
import { convertCurrencyInputs, convertMassInputs } from 'utils/units'
import { valuesType } from 'utils/values'

import { NumberField } from './NumberField'
import { ToggleButton } from './ToggleButton'
import { ToggleButtons } from './ToggleButtons'

import pdfIcon from '../assets/pdf.png'
import emailIcon from '../assets/email.png'


export type NotesState = {
  selectedDate: Date|null
  setSelectedDate: (date: Date|null) => void
  farm: string
  setFarm: (farm: string) => void
  nutritionist: string
  setNutritionist: (nutritionist: string) => void
  notes: string
  setNotes: (notes: string) => void
  isEmail: boolean
  setIsEmail: (isEmail: boolean) => void
  email: string
  setEmail: (email: string) => void
}


export function Controls(props: {
  calculate: (values: valuesType) => void
  currencies: schema.Currency[]
  feedingRates: schema.ProductFeedingRates
  initialCurrency: schema.Currency
  isPdf: boolean
  pdfRef?: React.RefObject<HTMLDivElement>
  products: schema.Product[]
  notesState: NotesState
  standards: schema.Standards
}) {
  const t = React.useContext(translationsContext)
  const [emailSent, setEmailSent] = React.useState<boolean|undefined>(false)
  const [pdfDialogOpen, setPdfDialogOpen] = React.useState(false)

  const { initialValues, setValues, values } = useFormikContext<valuesType>()

  const currency = props.currencies.find(c => c.id === values.currencyId) as schema.Currency

  React.useEffect(() => {
    if (!pdfDialogOpen) {
      setEmailSent(false)
    }
  }, [pdfDialogOpen])

  async function generatePdf() {
    if (!props.pdfRef || !props.pdfRef.current) return

    const canvas = await html2canvas(props.pdfRef.current, {useCORS: true})
    const pdf = new JsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [canvas.width, canvas.height],
    })
    const imgData = canvas.toDataURL('image/png')
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height)
    return pdf
  }

  async function handleDownloadPdf() {
    const pdf = await generatePdf()
    if (!pdf) return
    pdf.save('report.pdf')
    setPdfDialogOpen(false)
  }

  async function sendEmailPdf() {
    setEmailSent(undefined)
    const pdf = await generatePdf()
    if (!pdf) return
    const formData = new FormData()
    formData.append('file', pdf.output('blob'), 'report.pdf')
    formData.append('email', props.notesState.email)
    await fetch(
      '/api/send-email',
      {
        body: formData,
        headers: {'X-CSRFToken': window.GLOBAL_CONFIG.CSRF},
        method: 'POST',
      },
    )
    setEmailSent(true)
  }

  return (
    <mui.Grid container spacing={2} mt={1}>
      <mui.Grid container item spacing={2.5}>
        <mui.Grid container item xs={props.isPdf ? 4 : 12} spacing={2.5}>
          <mui.Grid item xs={props.isPdf ? 12 : 4.2} sx={{mt: -.5}}>
            <NumberField
              calculate={props.calculate}
              name="aabCents"
              label={t('calc__dashboard__field__aab')}
              onBlur={(e: any) => {if (e.target.value === '') setValues({...values, aabCents: 0}).then()}}
              fullWidth
              InputProps={{
                size: 'small',
                endAdornment: <mui.InputAdornment position="end">
                  <mui.Typography sx={{fontSize: '.6rem', color: '#000!important'}}>
                    {t('calc__dashboard__field__aab_units')}
                  </mui.Typography>
                </mui.InputAdornment>,
              }}
              sx={{
                mt: '2px',
                '& label': {
                  color: '#006600!important',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  left: '50%',
                  mt: props.isPdf ? '-.5rem' : '-.2rem',
                  transform: 'translate(-50%, -9px) scale(0.75)',
                  transformOrigin: 'top center',
                },
                '& .MuiInputBase-root': {
                  pt: props.isPdf ? '8px' : 0,
                  backgroundColor: '#D9F2D0',
                  color: '#006600',
                  pr: '8px',
                },
                '& .MuiInputAdornment-root p': {
                  color: '#006600',
                  mt: props.isPdf ? '-8px' : 0,
                },
                '& fieldset': {
                  borderColor: '#006600!important',
                  textAlign: 'center',
                  ...(props.isPdf ? {
                    top: 0,
                    '& span': {display: 'none'},
                    '& legend': {display: 'none'},
                  } : {}),
                },
                '& input': {
                  pl: '10px',
                  py: props.isPdf ? 0 : .5,
                },
              }}
            />
            <mui.Typography sx={{fontSize: '0.6rem', color: '#006600'}}>
              {t('calc__dashboard__field__aab_hint')}
            </mui.Typography>
          </mui.Grid>
          {(!props.isPdf || values.isPlusDmi) &&
            <mui.Grid item xs={props.isPdf ? 12 : 3.8}>
              <mui.Box sx={{position: 'relative'}}>
                <mui.Box component="label" sx={{
                  position: 'absolute',
                  top: 'calc(-1rem - 5px)',
                  left: '50%',
                  transform: 'translateX(-52%)',
                  px: .5,
                  color: '#EC671F',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}><span style={{color: '#FFB66D'}}>+</span>{t('calc__dashboard__field__dmi')}</mui.Box>
                <ToggleButton
                  calculate={props.calculate}
                  name="isPlusDmi"
                  label={<mui.Box sx={{my: '5px', lineHeight: 1.25}}>
                    {t('calc__dashboard__field__dmi_label')}
                  </mui.Box>}
                  sx={{
                    p: 0,
                    height: '45px',
                    width: '100%',
                    ...volumeSx(props.isPdf, '#EA8D00', '#FEF5F0'),
                  }}
                />
              </mui.Box>
            </mui.Grid>
          }
          <mui.Grid item xs={props.isPdf ? 12 : 4}>
            {props.isPdf
              ? <>
                <mui.Typography
                  sx={{
                    color: '#FF6699',
                    fontSize: '1rem',
                    mt: 'calc(-1rem - 3px)',
                    mb: '-2px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  {t('calc__dashboard__field__herd')}
                </mui.Typography>
                <mui.Typography
                  sx={{
                    backgroundColor: '#FFE6FF',
                    border: '1px solid #FF6699',
                    borderRadius: '4px',
                    py: '6px',
                    pb: 1,
                    color: '#FF6699',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  {values.herdSize}
                </mui.Typography>
              </>
              : <NumberField
                  calculate={props.calculate}
                  name="herdSize"
                  label={t('calc__dashboard__field__herd')}
                  fullWidth
                  InputProps={{
                    size: 'small',
                    endAdornment: <mui.InputAdornment position="end">
                      <mui.Typography sx={{fontSize: '.6rem', color: '#000!important'}}>
                        {t('calc__dashboard__field__herd_units')}
                      </mui.Typography>
                    </mui.InputAdornment>,
                  }}
                  sx={{
                    '& label': {
                      color: '#FF6699!important',
                      fontWeight: 'bold',
                      mt: '-.25rem',
                      left: '50%',
                      transform: 'translate(-50%, -9px) scale(0.75)',
                      transformOrigin: 'top center',
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFE6FF',
                      color: '#FF6699',
                      pr: '8px',
                    },
                    '& .MuiInputAdornment-root p': {
                      color: '#FF6699',
                    },
                    '& fieldset': {
                      borderColor: '#FF6699!important',
                      textAlign: 'center',
                    },
                    '& input': {
                      pl: '0px',
                      textAlign: 'center',
                    },
                  }}
                />
            }
          </mui.Grid>
        </mui.Grid>
        {props.isPdf &&
          <mui.Grid container item xs={8}>
            <mui.Grid item xs={12} sx={{
              backgroundColor: '#F5F5F5',
              color: '#7F7F7F',
              borderRadius: '20px',
              padding: 2,
              lineHeight: 1.15,
            }}>
              <mui.Typography
                style={{whiteSpace: 'pre-wrap'}}
                sx={{
                  fontSize: '0.8rem',
                  lineHeight: 1.15,
                }}
              >
                <b>{t('calc__pdf_form__field__farm')}:</b> {props.notesState.farm}<br/>
                <b>{t('calc__pdf_form__field__nutritionist')}:</b> {props.notesState.nutritionist}<br/>
                <b>{t('calc__pdf_form__field__accessed_on')}:</b> {props.notesState.selectedDate?.toLocaleDateString()}<br/><br/>
                <b>{t('calc__pdf_form__field__notes')}:</b> {props.notesState.notes}
              </mui.Typography>
            </mui.Grid>
          </mui.Grid>
        }
      </mui.Grid>
      {!props.isPdf && <>
        <mui.Grid item xs={12}>
          <mui.Stack gap={3} direction="row">
            <ToggleButtons
              calculate={props.calculate}
              fullWidth
              isPdf={props.isPdf}
              name="resultsUnitsAmount"
              optionsRows={[[
                {label: t('calc__dashboard__field__units_cow'), value: schema.ResultsUnitsAmount.COW},
                {label: t('calc__dashboard__field__units_herd'), value: schema.ResultsUnitsAmount.HERD},
              ]]}
              sx={{justifyContent: 'center'}}
            />
            <ToggleButtons
              calculate={props.calculate}
              fullWidth
              isPdf={props.isPdf}
              name="resultsUnitsPeriod"
              optionsRows={[[
                {label: t('calc__dashboard__field__units_day'), value: schema.ResultsUnitsPeriod.DAY},
                {label: t('calc__dashboard__field__units_mo'), value: schema.ResultsUnitsPeriod.MONTH},
                {label: t('calc__dashboard__field__units_yr'), value: schema.ResultsUnitsPeriod.YEAR},
              ]]}
              sx={{justifyContent: 'center'}}
            />
          </mui.Stack>
        </mui.Grid>
        <mui.Grid item xs={12}>
          <mui.Stack gap={3} direction="row" alignItems="center" sx={{mt: -.5}}>
            <mui.Button
              onClick={() => {
                const initialFeedingRates = props.feedingRates
                const valuesToPreserve = {
                  units: values.units,
                  resultsUnitsAmount: values.resultsUnitsAmount,
                  resultsUnitsPeriod: values.resultsUnitsPeriod,
                  currencyId: values.currencyId,
                  herdSize: values.herdSize,
                  investmentProductGramsPerCowDayCloseUp: initialFeedingRates!.closeUp,
                  investmentProductGramsPerCowDayFresh: initialFeedingRates!.fresh,
                  investmentProductGramsPerCowDayEarlyMidLact: initialFeedingRates!.earlyMidLact,
                  investmentProductGramsPerCowDayLateLact: initialFeedingRates!.lateLact,
                  productId: values.productId,
                  regionId: values.regionId,
                }

                function update(values: valuesType) {
                  convertCurrencyInputs(
                    props.products,
                    (values: valuesType) => setValues(values, false),
                    props.initialCurrency.conversionRate,
                    currency,
                    values,
                  ).then(() => props.calculate(values as schema.CalculationInput))
                }

                if (values.units === props.standards.region.units as string) {
                  update({...initialValues, ...valuesToPreserve})
                } else {
                  convertMassInputs(
                    (values: valuesType) => setValues(values, false),
                    values.units,
                    {...initialValues, ...valuesToPreserve},
                    currency.code,
                    false
                  )
                  .then(convertedValues => {
                    update(convertedValues)
                  })
                }
              }}
              sx={{
                width: '70px',
                py: '1.5px',
                fontWeight: 'bold',
                textTransform: 'none',
                ...volumeSx(props.isPdf, '#D9E5F8', '#0F4861'),
            }}
            >
              {t('calc__dashboard__button__reset')}
            </mui.Button>
            <ToggleButtons
              calculate={props.calculate}
              name="units"
              isPdf={props.isPdf}
              fullWidth
              onChange={async (e, newValue) => await convertMassInputs((values: valuesType) => setValues(values, false), newValue as schema.Units, values, currency.code)}
              optionsRows={[
                [
                  {label: t('calc__dashboard__field__units_lb'), value: schema.Units.IMPERIAL},
                  {label: t('calc__dashboard__field__units_kg'), value: schema.Units.METRIC},
                ],
              ]}
              sx={{justifyContent: 'center'}}
            />
            {!props.isPdf &&
              <mui.Box>
                <mui.IconButton sx={{py: 0}} onClick={() => {
                  props.notesState.setIsEmail(false)
                  setPdfDialogOpen(true)
                }}>
                  <img src={pdfIcon} alt="Download PDF" height="50"/>
                </mui.IconButton>
                <mui.IconButton sx={{py: 0}} onClick={() => {
                  props.notesState.setIsEmail(true)
                  setPdfDialogOpen(true)
                }}>
                  <img src={emailIcon} alt="Email PDF" height="50"/>
                </mui.IconButton>
              </mui.Box>
            }
          </mui.Stack>
        </mui.Grid>
      </>}
      <mui.Dialog
        component="form"
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        onSubmit={e => {
          e.preventDefault()
          if (props.notesState.isEmail) {
            sendEmailPdf().then()
          } else {
            handleDownloadPdf().then()
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        {!emailSent
          ? <mui.DialogContent>
              <mui.TextField
                margin="dense"
                label={t('calc__pdf_form__field__farm')}
                fullWidth
                variant="standard"
                value={props.notesState.farm}
                onChange={(v) => props.notesState.setFarm(v.target.value.slice(0, 20))}
              />
              <mui.TextField
                margin="dense"
                label={t('calc__pdf_form__field__nutritionist')}
                fullWidth
                variant="standard"
                value={props.notesState.nutritionist}
                onChange={(v) => props.notesState.setNutritionist(v.target.value.slice(0, 15))}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('calc__pdf_form__field__accessed_on')}
                  value={props.notesState.selectedDate}
                  onChange={(date) => props.notesState.setSelectedDate(date)}
                  sx={{mt: 1}}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'standard',
                    },
                  }}
                />
              </LocalizationProvider>
              <mui.TextField
                margin="dense"
                label={t('calc__pdf_form__field__notes')}
                fullWidth
                variant="standard"
                multiline
                rows={3}
                value={props.notesState.notes}
                onChange={(v) => props.notesState.setNotes(v.target.value)}
              />
              {props.notesState.isEmail &&
                <mui.TextField
                  margin="dense"
                  type="email"
                  label={t('calc__pdf_form__field__email')}
                  fullWidth
                  required
                  variant="standard"
                  value={props.notesState.email}
                  onChange={(v) => props.notesState.setEmail(v.target.value)}
                />
              }
            </mui.DialogContent>
          : <>
              <mui.DialogTitle>{t('calc__pdf_form__success__title')}</mui.DialogTitle>
              <mui.DialogContent>{t('calc__pdf_form__success__body')} {props.notesState.email}</mui.DialogContent>
            </>
        }

        {!emailSent
          ? <mui.DialogActions sx={{p: 3, pt: 1}}>
              <mui.Button color="error" onClick={() => setPdfDialogOpen(false)}>{t('calc__pdf_form__button__cancel')}</mui.Button>
              <mui.Button
                color="success"
                variant="contained"
                type="submit"
                disabled={emailSent !== false}
              >
                {props.notesState.isEmail
                  ? t('calc__pdf_form__button__send')
                  : t('calc__pdf_form__button__download')
                }
              </mui.Button>
            </mui.DialogActions>
          : <mui.DialogActions sx={{p: 3, pt: 1}}>
              <mui.Button onClick={() => setPdfDialogOpen(false)}>{t('calc__pdf_form__button__close')}</mui.Button>
            </mui.DialogActions>
        }
      </mui.Dialog>
    </mui.Grid>
  )
}